import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Leaf, Calculator, TrendingUp, Zap, DollarSign, Loader2, Download, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";

type CalculatorType = "co2" | "fleet" | "cost" | "energy" | "roi";

interface CalculatorInputs {
  co2: { vehicleCount: number; dailyDistance: number; workingDays: number };
  fleet: { areaSize: number; dailyTrips: number; peakHours: number };
  cost: { fleetSize: number; annualMileage: number; fuelPrice: number };
  energy: { fleetSize: number; avgTripDistance: number; chargerType: string };
  roi: { investment: number; fleetSize: number; operatingCost: number };
}

const calculatorConfig = [
  { id: "co2" as CalculatorType, label: "CO₂ Savings", icon: Leaf, color: "from-eco-green to-teal-bright" },
  { id: "fleet" as CalculatorType, label: "Fleet Optimizer", icon: Calculator, color: "from-sky-blue to-purple" },
  { id: "cost" as CalculatorType, label: "Cost Comparison", icon: DollarSign, color: "from-primary to-amber-glow" },
  { id: "energy" as CalculatorType, label: "Energy Estimator", icon: Zap, color: "from-purple to-rose" },
  { id: "roi" as CalculatorType, label: "ROI Calculator", icon: TrendingUp, color: "from-teal-bright to-eco-green" },
];

const SustainabilityCalculator = () => {
  const [activeTab, setActiveTab] = useState<CalculatorType>("co2");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>("");
  const [inputs, setInputs] = useState<CalculatorInputs>({
    co2: { vehicleCount: 10, dailyDistance: 50, workingDays: 250 },
    fleet: { areaSize: 100, dailyTrips: 500, peakHours: 4 },
    cost: { fleetSize: 20, annualMileage: 15000, fuelPrice: 2.5 },
    energy: { fleetSize: 15, avgTripDistance: 8, chargerType: "Level 2" },
    roi: { investment: 100000, fleetSize: 25, operatingCost: 5000 },
  });

  const updateInput = (type: CalculatorType, field: string, value: number | string) => {
    setInputs((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleCalculate = async () => {
    setIsLoading(true);
    setResult("");

    try {
      const response = await supabase.functions.invoke("sustainability-calculator", {
        body: { type: activeTab, inputs: inputs[activeTab] },
      });

      if (response.error) throw response.error;
      setResult(response.data.result);
    } catch (error) {
      console.error("Calculation error:", error);
      setResult("An error occurred while calculating. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCalculatorLabel = (type: CalculatorType) => {
    return calculatorConfig.find((c) => c.id === type)?.label || type;
  };

  const getCurrentColor = () => {
    return calculatorConfig.find((c) => c.id === activeTab)?.color || "from-primary to-amber-glow";
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;
    let yPosition = 20;

    // Header
    doc.setFillColor(26, 54, 54);
    doc.rect(0, 0, pageWidth, 45, "F");
    
    doc.setTextColor(212, 175, 55);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("SDC Gulf", margin, 25);
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text("Sustainability Report", margin, 35);

    yPosition = 60;

    // Report Title
    doc.setTextColor(26, 54, 54);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(`${getCalculatorLabel(activeTab)} Analysis`, margin, yPosition);
    yPosition += 10;

    // Date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-US", { 
      year: "numeric", month: "long", day: "numeric" 
    })}`, margin, yPosition);
    yPosition += 15;

    // Input Parameters Section
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(26, 54, 54);
    doc.text("Input Parameters", margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    const currentInputs = inputs[activeTab];
    Object.entries(currentInputs).forEach(([key, value]) => {
      const formattedKey = key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
      doc.text(`• ${formattedKey}: ${value}`, margin + 5, yPosition);
      yPosition += 6;
    });

    yPosition += 10;

    // AI Analysis Results Section
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(26, 54, 54);
    doc.text("AI Analysis Results", margin, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);

    // Split result into lines that fit the page width
    const lines = doc.splitTextToSize(result, contentWidth);
    lines.forEach((line: string) => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, margin, yPosition);
      yPosition += 5;
    });

    // Footer
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount} | SDC Gulf Sustainability Report`,
        pageWidth / 2,
        290,
        { align: "center" }
      );
    }

    doc.save(`sustainability-report-${activeTab}-${Date.now()}.pdf`);
  };

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/50 to-secondary/30" />
      <div className={`absolute -left-40 top-1/4 w-80 h-80 rounded-full bg-gradient-to-br ${getCurrentColor()} blur-3xl opacity-20 transition-all duration-1000`} />
      <div className={`absolute -right-40 bottom-1/4 w-80 h-80 rounded-full bg-gradient-to-br ${getCurrentColor()} blur-3xl opacity-20 transition-all duration-1000`} />
      
      <div className="container mx-auto px-6 relative z-10">
        <VibrantSectionHeader
          badge="AI-Powered Tools"
          title="Sustainability Calculator"
          description="Use our AI-powered calculators to estimate your environmental impact, optimize your fleet, and plan your transition to sustainable mobility."
          badgeColor="purple"
        />

        <Card className="max-w-4xl mx-auto bg-card/80 backdrop-blur-sm border-border/50 shadow-2xl">
          <CardHeader className="pb-2">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as CalculatorType)}>
              <TabsList className="grid w-full grid-cols-5 bg-secondary/50 p-1 rounded-xl">
                {calculatorConfig.map((calc) => (
                  <TabsTrigger
                    key={calc.id}
                    value={calc.id}
                    className={`flex items-center gap-2 text-xs md:text-sm rounded-lg transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:${calc.color} data-[state=active]:text-white data-[state=active]:shadow-lg`}
                  >
                    <calc.icon className="w-4 h-4" />
                    <span className="hidden md:inline">{calc.label}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardHeader>

          <CardContent className="space-y-6 pt-6">
            {/* CO2 Savings Calculator */}
            {activeTab === "co2" && (
              <div className="space-y-4">
                <CardDescription className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-eco-green" />
                  Calculate how much CO₂ your organization can save by switching to electric micro mobility.
                </CardDescription>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Number of Vehicles</Label>
                    <Input
                      type="number"
                      value={inputs.co2.vehicleCount}
                      onChange={(e) => updateInput("co2", "vehicleCount", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-eco-green focus:ring-eco-green/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Daily Distance (km)</Label>
                    <Input
                      type="number"
                      value={inputs.co2.dailyDistance}
                      onChange={(e) => updateInput("co2", "dailyDistance", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-eco-green focus:ring-eco-green/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Working Days/Year</Label>
                    <Input
                      type="number"
                      value={inputs.co2.workingDays}
                      onChange={(e) => updateInput("co2", "workingDays", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-eco-green focus:ring-eco-green/20"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Fleet Optimizer */}
            {activeTab === "fleet" && (
              <div className="space-y-4">
                <CardDescription className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-sky-blue" />
                  Get AI recommendations for optimal fleet size based on your coverage area and demand.
                </CardDescription>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Area Size (km²)</Label>
                    <Input
                      type="number"
                      value={inputs.fleet.areaSize}
                      onChange={(e) => updateInput("fleet", "areaSize", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-sky-blue focus:ring-sky-blue/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Expected Daily Trips</Label>
                    <Input
                      type="number"
                      value={inputs.fleet.dailyTrips}
                      onChange={(e) => updateInput("fleet", "dailyTrips", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-sky-blue focus:ring-sky-blue/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Peak Hours/Day</Label>
                    <Input
                      type="number"
                      value={inputs.fleet.peakHours}
                      onChange={(e) => updateInput("fleet", "peakHours", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-sky-blue focus:ring-sky-blue/20"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Cost Comparison */}
            {activeTab === "cost" && (
              <div className="space-y-4">
                <CardDescription className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  Compare total cost of ownership between traditional vehicles and electric micro mobility.
                </CardDescription>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Fleet Size</Label>
                    <Input
                      type="number"
                      value={inputs.cost.fleetSize}
                      onChange={(e) => updateInput("cost", "fleetSize", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-primary focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Annual Mileage (km)</Label>
                    <Input
                      type="number"
                      value={inputs.cost.annualMileage}
                      onChange={(e) => updateInput("cost", "annualMileage", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-primary focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Fuel Price ($/L)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={inputs.cost.fuelPrice}
                      onChange={(e) => updateInput("cost", "fuelPrice", parseFloat(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-primary focus:ring-primary/20"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Energy Estimator */}
            {activeTab === "energy" && (
              <div className="space-y-4">
                <CardDescription className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple" />
                  Estimate your energy requirements and charging infrastructure needs.
                </CardDescription>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Fleet Size</Label>
                    <Input
                      type="number"
                      value={inputs.energy.fleetSize}
                      onChange={(e) => updateInput("energy", "fleetSize", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-purple focus:ring-purple/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Avg Trip Distance (km)</Label>
                    <Input
                      type="number"
                      value={inputs.energy.avgTripDistance}
                      onChange={(e) => updateInput("energy", "avgTripDistance", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-purple focus:ring-purple/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Charger Type</Label>
                    <Input
                      type="text"
                      value={inputs.energy.chargerType}
                      onChange={(e) => updateInput("energy", "chargerType", e.target.value)}
                      placeholder="Level 2, DC Fast, etc."
                      className="bg-secondary/50 border-border/50 focus:border-purple focus:ring-purple/20"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ROI Calculator */}
            {activeTab === "roi" && (
              <div className="space-y-4">
                <CardDescription className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-teal-bright" />
                  Calculate your return on investment timeline for sustainable mobility adoption.
                </CardDescription>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Initial Investment ($)</Label>
                    <Input
                      type="number"
                      value={inputs.roi.investment}
                      onChange={(e) => updateInput("roi", "investment", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-teal-bright focus:ring-teal-bright/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Fleet Size</Label>
                    <Input
                      type="number"
                      value={inputs.roi.fleetSize}
                      onChange={(e) => updateInput("roi", "fleetSize", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-teal-bright focus:ring-teal-bright/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Monthly Operating Cost ($)</Label>
                    <Input
                      type="number"
                      value={inputs.roi.operatingCost}
                      onChange={(e) => updateInput("roi", "operatingCost", parseInt(e.target.value) || 0)}
                      className="bg-secondary/50 border-border/50 focus:border-teal-bright focus:ring-teal-bright/20"
                    />
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={handleCalculate} 
              disabled={isLoading} 
              className={`w-full bg-gradient-to-r ${getCurrentColor()} hover:opacity-90 text-white font-semibold shadow-lg transition-all duration-300 hover:scale-[1.02]`}
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Analyzing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-5 w-5" />
                  Calculate with AI
                </>
              )}
            </Button>

            {result && (
              <Card className="bg-gradient-to-br from-eco-green/10 to-teal-bright/5 border-eco-green/20 shadow-lg">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-eco-green to-teal-bright flex items-center justify-center">
                      <Leaf className="w-4 h-4 text-white" />
                    </div>
                    AI Analysis Results
                  </CardTitle>
                  <Button onClick={generatePDF} variant="outline" size="sm" className="gap-2 border-eco-green/30 hover:bg-eco-green/10 hover:text-eco-green">
                    <Download className="w-4 h-4" />
                    Download PDF
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-invert max-w-none text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {result}
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default SustainabilityCalculator;
